import type { Scene } from "@babylonjs/core/scene";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { Observer } from "@babylonjs/core/Misc/observable";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { freezeStaticMeshes } from "../staticOptimization";
import { tagAs } from "../PlaceholderTag";
import type { CanopySurfaces } from "./CanopySurfaces";

/** Optional generated treetop shell, with one lifetime for art and casters. */
export class CanopyScenery {
  readonly ready: Promise<boolean>;
  private retired = false;
  private instance: InstantiatedModel | null = null;
  private casters: AbstractMesh[] = [];
  private observer: Observer<Scene> | null;

  constructor(private readonly scene: Scene, private readonly surfaces: CanopySurfaces,
    private readonly shadows: ShadowGenerator) {
    this.observer = scene.onDisposeObservable.addOnce(() => this.dispose());
    this.ready = this.load();
  }

  private async load(): Promise<boolean> {
    let candidate: InstantiatedModel | null = null;
    try {
      candidate = await getAssetManager().instantiateSharedModel(this.scene,
        "/assets/models/environment/canopy_observatory.glb");
      if (this.retired || this.scene.isDisposed) { candidate.dispose(); return false; }
      const tops = this.surfaces.validate(candidate.meshes);
      for (const mesh of candidate.meshes) {
        mesh.isPickable = false; mesh.receiveShadows = !mesh.name.includes("night_sky");
        tagAs(mesh, "final");
      }
      // Rear crowns, sky and observatory ribs never enlarge the shadow fit.
      this.casters = tops.filter(m => !m.name.endsWith("Canopy_ground_top"));
      for (const mesh of this.casters) this.shadows.addShadowCaster(mesh);
      freezeStaticMeshes(candidate.meshes, { freezeMaterials: false });
      this.surfaces.adopt();
      this.instance = candidate;
      return true;
    } catch (error) {
      for (const mesh of this.casters) this.shadows.removeShadowCaster(mesh);
      this.casters = [];
      candidate?.dispose(); this.surfaces.restore();
      if (!this.retired && !this.scene.isDisposed)
        console.warn("[Canopy] Scenery unavailable; retaining opaque physical surfaces:", error);
      return false;
    }
  }

  dispose(): void {
    if (this.retired) return;
    this.retired = true;
    if (this.observer) this.scene.onDisposeObservable.remove(this.observer);
    this.observer = null;
    for (const mesh of this.casters) this.shadows.removeShadowCaster(mesh);
    this.casters = [];
    this.instance?.dispose(); this.instance = null;
    this.surfaces.restore();
  }
}
